<template>
    <div class="shadow uses-tailwind">
        <div
            class="p-2 font-bold text-white transition-all relative"
            :class="{
                'bg-red-700': isOverTime && !isLoading,
                'bg-orange-600': isAlmostOverTime && !isLoading,
                'bg-lime-600': !isOverTime && !isLoading,
            }"
        >
            <div
                class="absolute inset-0 bg-white text-black flex items-center justify-center gap-2"
                v-show="isLoading"
            >
                <spinner class="h-4" />
                <p class="text-base font-normal">
                    {{ trans('timer.loading') }}
                </p>
            </div>
            <p class="text-left text-xl">{{ formatSeconds(totalSeconds) }}</p>
            <p class="text-5xl text-center py-5">
                {{ formatSeconds(remainingSeconds) }}
            </p>
        </div>
        <div
            class="grid divide-x text-xl border-t"
            :class="{
                'grid-cols-3': canReportError,
                'grid-cols-2': !canReportError,
            }"
        >
            <button
                v-if="!isPaused"
                :disabled="!canPause || isFinished || isLoading"
                @click="pause"
                class="text-2xl bg-white hover:bg-zinc-50 text-black p-2 h-[70px] disabled:hover:bg-white disabled:cursor-not-allowed disabled:opacity-20"
            >
                {{ trans('actions.pause') }}
            </button>
            <button
                v-if="isPaused"
                :disabled="!canPause || isFinished || isLoading"
                @click="resume"
                class="text-2xl bg-white hover:bg-zinc-50 text-black p-2 h-[70px] disabled:hover:bg-white disabled:cursor-not-allowed disabled:opacity-20"
            >
                {{ trans('actions.resume') }}
            </button>
            <button
                :disabled="isFinished || isLoading"
                @click="finish"
                class="text-2xl bg-white hover:bg-zinc-50 text-black p-2 h-[70px] disabled:hover:bg-white disabled:cursor-not-allowed disabled:opacity-20"
            >
                {{ trans('actions.finish') }}
            </button>
            <button
                v-if="canReportError"
                :disabled="!canReportError || isFinished || isLoading"
                @click="reportError"
                class="text-2xl bg-white hover:bg-zinc-50 text-black p-2 h-[70px] disabled:hover:bg-white disabled:cursor-not-allowed disabled:opacity-20"
            >
                {{ trans('actions.report_error') }}
            </button>
        </div>
    </div>
</template>

<script>
import Spinner from '../components/loader/Spinner';

export default {
    components: {
        Spinner,
    },

    props: {
        totalSeconds: {
            required: true,
            type: Number,
        },
        previouslyElapsedSeconds: {
            required: false,
            type: Number,
            default: 0,
        },
        canPause: {
            required: false,
            type: Boolean,
            default: true,
        },
        canReportError: {
            required: false,
            type: Boolean,
            default: false,
        },
        startWhenMounted: {
            required: false,
            type: Boolean,
            default: false,
        },
        isLoading: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            status: 'paused',
            hasGoneOverTime: false,
            elapsedSeconds: 0,
            lastFrameTime: null,
            timerCallback: null,
            startedAt: null,
            stoppedAt: null,
        };
    },

    computed: {
        remainingSeconds() {
            return this.totalSeconds - this.elapsedSeconds;
        },
        isRunning() {
            return this.status === 'running';
        },
        isPaused() {
            return this.status === 'paused';
        },
        isFinished() {
            return this.status === 'finished';
        },
        isOverTime() {
            return this.elapsedSeconds >= this.totalSeconds;
        },
        isAlmostOverTime() {
            return (
                this.remainingSeconds / this.totalSeconds <= 0.1 &&
                !this.isOverTime
            );
        },
        emitPayload() {
            return {
                elapsedSeconds:
                    Math.ceil(this.elapsedSeconds) -
                    this.previouslyElapsedSeconds,
                startedAt: this.startedAt,
                stoppedAt: this.stoppedAt,
                totalElapsedSeconds: Math.ceil(this.elapsedSeconds),
                hasGoneOverTime: this.hasGoneOverTime,
            };
        },
    },

    watch: {
        elapsedSeconds() {
            if (this.isOverTime && !this.hasGoneOverTime) {
                this.hasGoneOverTime = true;
                this.$emit('has-gone-over-time');
            }
        },
        previouslyElapsedSeconds() {
            this.elapsedSeconds = this.previouslyElapsedSeconds;
        },
        status() {
            if (this.isRunning) {
                this.lastFrameTime = performance.now();
                this.timerCallback = requestAnimationFrame(this.updateTime);
            } else {
                cancelAnimationFrame(this.timerCallback);
            }
        },
    },

    emits: [
        'started',
        'paused',
        'resumed',
        'finished',
        'error',
        'has-gone-over-time',
    ],

    mounted() {
        this.elapsedSeconds = this.previouslyElapsedSeconds;

        if (this.startWhenMounted) {
            this.$nextTick(() => {
                this.start();
            });
        }
    },

    methods: {
        updateTime(currentTime) {
            const deltaTime = (currentTime - this.lastFrameTime) / 1000;
            this.lastFrameTime = currentTime;
            this.elapsedSeconds += deltaTime;

            if (this.isRunning) {
                this.timerCallback = requestAnimationFrame(this.updateTime);
            }
        },

        start(emit = true) {
            this.lastFrameTime = performance.now();
            this.timerCallback = requestAnimationFrame(this.updateTime);
            this.status = 'running';
            // Floor the date to the nearest second
            this.startedAt = new Date(Math.floor(Date.now() / 1000) * 1000);

            if (emit) {
                this.$emit('started');
            }
        },

        pause(emit = true) {
            this.status = 'paused';
            cancelAnimationFrame(this.timerCallback);
            // Ceil the date to the nearest second
            this.stoppedAt = new Date(Math.ceil(Date.now() / 1000) * 1000);

            if (emit) {
                this.$emit('paused', this.emitPayload);
            }
        },

        resume(emit = true) {
            this.start(false);

            if (emit) {
                this.$emit('resumed');
            }
        },

        finish(emit = true) {
            this.status = 'finished';
            cancelAnimationFrame(this.timerCallback);
            this.stoppedAt = new Date();

            if (emit) {
                this.$emit('finished', this.emitPayload);
            }
        },

        reportError(emit = true) {
            this.pause(false);

            if (emit) {
                this.$emit('has-reported-error', this.emitPayload);
            }
        },

        formatSeconds(timeInSeconds) {
            timeInSeconds = Math.floor(timeInSeconds);
            const minutes = Math.floor(Math.abs(timeInSeconds) / 60)
                .toString()
                .padStart(2, '0');
            const seconds = (Math.abs(timeInSeconds) % 60)
                .toString()
                .padStart(2, '0');

            return `${timeInSeconds < 0 ? '-' : ''}${minutes}:${seconds}`;
        },
    },
};
</script>
